import { Mutex } from 'async-mutex';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { env } from '@/shared/config/env';
import i18n from '@/shared/i18n';
import { credentialsUpdated, sessionCleared, type UserProfile } from '@/shared/lib/redux/actions';
import type { RootStateShape } from '@/shared/lib/redux/rootStateShape';
import { TAG_TYPES } from './tags';

const REFRESH_PATH = 'auth/refresh';

function requestUrl(args: string | FetchArgs): string {
  return (typeof args === 'string' ? args : args.url).replace(/^\//, '');
}

function isRefreshRequest(args: string | FetchArgs): boolean {
  return requestUrl(args) === REFRESH_PATH;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  credentials: 'include', // sends the httpOnly refresh cookie
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootStateShape;
    if (state.auth.accessToken) {
      headers.set('Authorization', `Bearer ${state.auth.accessToken}`);
    }
    headers.set('Accept-Language', i18n.language);
    return headers;
  },
});

const mutex = new Mutex();

/** On a 401, refreshes the access token (via the `rt` cookie) at most once at a time — concurrent
 *  requests that 401 while a refresh is already in flight wait for it instead of each starting
 *  their own — then retries the original request. A 401 on the refresh call itself (or on any
 *  request while the refresh mutex is held and fails) clears the session instead of looping. */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401 && !isRefreshRequest(args)) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await rawBaseQuery(
          { url: REFRESH_PATH, method: 'POST' },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          const { accessToken, user } = refreshResult.data as {
            accessToken: string;
            user: UserProfile;
          };
          api.dispatch(credentialsUpdated({ accessToken, user }));
          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          api.dispatch(sessionCleared());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};

function isGetRequest(args: string | FetchArgs): boolean {
  return typeof args === 'string' || (args.method ?? 'GET').toUpperCase() === 'GET';
}

/** Only a transient network-level failure is worth retrying — an HTTP error status (401, 404,
 *  500, ...) will fail again identically, so retrying it would just hammer the server (this is
 *  exactly what caused a request-storm bug during Stage 6 verification: retrying every 401 here
 *  raced `baseQueryWithReauth`'s own single refresh-and-retry, compounding into dozens of
 *  requests/sec). */
function isTransientNetworkError(error: unknown): boolean {
  const status = (error as Partial<FetchBaseQueryError> | undefined)?.status;
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
}

const MAX_RETRIES = 3;

const baseQueryWithRetry = retry(baseQueryWithReauth, {
  retryCondition: (error: unknown, args: string | FetchArgs, { attempt }) =>
    attempt <= MAX_RETRIES && isGetRequest(args) && isTransientNetworkError(error),
});

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: TAG_TYPES,
  endpoints: () => ({}),
});
